import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listAPI, guessAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";
import Timer from "../components/Timer";

const ListDetail = () => {
  const { slug } = useParams();
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [guess, setGuess] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("alphabetical");

  // Difficulty mode
  const [difficultyMode, setDifficultyMode] = useState(() => {
    const saved = localStorage.getItem(`difficulty_${slug}`);
    return saved || "casual";
  });
  const [showDifficultyInfo, setShowDifficultyInfo] = useState(false);

  // Timed mode states
  const [gameMode, setGameMode] = useState(null); // null = not started, 'normal', 'timed'
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    // Restore saved timer when component mounts
    const savedTimer = localStorage.getItem(`timer_${slug}`);
    return savedTimer ? parseInt(savedTimer, 10) : 0;
  });
  const [showModeSelector, setShowModeSelector] = useState(true);

  usePageTitle(list?.title);

  // Set default sort to "rank" for ranked lists
  useEffect(() => {
    if (list && list.isRanked) {
      setSortOrder("rank");
    }
  }, [list]);

  useEffect(() => {
    localStorage.setItem(`difficulty_${slug}`, difficultyMode);
  }, [difficultyMode, slug]);

  useEffect(() => {
    loadList();
  }, [slug]);

  // Check if user has already started this list
  useEffect(() => {
    if (list) {
      if (list.guessedCount > 0) {
        const mode = list.isTimedMode ? "timed" : "normal";

        if (gameMode === null) {
          setGameMode(mode);
          setShowModeSelector(false);

          if (list.isTimedMode && !list.isCompleted) {
            // Use backend timer first, then localStorage as fallback
            const backendTimer = list.currentTimerSeconds || 0;
            const localTimer = localStorage.getItem(`timer_${slug}`);
            const timerToUse =
              backendTimer > 0
                ? backendTimer
                : localTimer
                  ? parseInt(localTimer, 10)
                  : 0;

            setElapsedSeconds(timerToUse);
            setIsTimerActive(true);
          }
        }
      } else if (list.guessedCount === 0 && gameMode === null) {
        if (!showModeSelector) {
          setShowModeSelector(true);
          setIsTimerActive(false);
          setElapsedSeconds(0);
        }
      }
    }
  }, [list]);

  // Save timer when user leaves the page, navigates away, or closes browser
  useEffect(() => {
    if (!isAuthenticated || !list || gameMode !== "timed" || list.isCompleted) {
      return;
    }

    const saveTimer = () => {
      if (isTimerActive && elapsedSeconds > 0) {
        // Use keepalive fetch to ensure request completes even if page is closing
        const token = localStorage.getItem("token");
        const apiUrl =
          process.env.REACT_APP_API_URL || "http://localhost:8080/api";
        const url = `${apiUrl}/guess/${list.id}/timer`;

        fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ elapsedSeconds }),
          keepalive: true,
        }).catch((err) => console.error("Failed to save timer:", err));
      }
    };

    // Save on component unmount (navigation to another page)
    return () => {
      saveTimer();
    };
  }, [isAuthenticated, list, gameMode, isTimerActive, elapsedSeconds]);

  const loadList = async () => {
    setLoading(true);
    try {
      const response = await listAPI.getListDetailBySlug(slug);
      setList(response.data);
    } catch (error) {
      console.error("Failed to load list:", error);
      setMessage("Failed to load list");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleModeSelect = (mode) => {
    setGameMode(mode);
    setShowModeSelector(false);

    if (mode === "timed") {
      // Timed mode ALWAYS uses relaxed difficulty
      setDifficultyMode("relaxed");
      localStorage.setItem(`difficulty_${slug}`, "relaxed");

      // Clear any old saved timer and start fresh
      localStorage.removeItem(`timer_${slug}`);
      setElapsedSeconds(0);
      setIsTimerActive(true);
    }
  };

  const handleTimeUpdate = useCallback(
    (seconds) => {
      setElapsedSeconds(seconds);
      // Save timer to localStorage on every tick
      localStorage.setItem(`timer_${slug}`, seconds.toString());
    },
    [slug],
  );

  const handleGuess = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setMessage("You must be logged in to guess!");
      setMessageType("error");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    if (!guess.trim()) return;

    if (!gameMode) {
      setMessage("Please select a game mode first!");
      setMessageType("error");
      return;
    }

    try {
      const response = await guessAPI.submitGuess(
        list.id,
        guess.trim(),
        difficultyMode,
        gameMode === "timed",
        gameMode === "timed" ? elapsedSeconds : null,
      );

      if (response.data.isCorrect) {
        setMessageType("success");
        setMessage(response.data.message || "Correct!");

        // If completed, stop timer and clean up localStorage
        if (response.data.isCompleted) {
          setIsTimerActive(false);
          localStorage.removeItem(`timer_${slug}`);
        }
      } else {
        setMessageType("error");
        setMessage(response.data.message || "Incorrect guess. Try again!");
      }

      // Clear guess field immediately for both correct and incorrect
      setGuess("");

      // UPDATE: Use response data if available to avoid full reload
      // This keeps the input focused and prevents mobile keyboard from closing
      // Use response data if available to avoid full reload
      if (response.data.updatedProgress) {
        setList((prevList) => ({
          ...prevList,
          ...response.data.updatedProgress,
        }));

        // Sync timer from backend response
        if (
          response.data.updatedProgress.isTimedMode &&
          response.data.updatedProgress.currentTimerSeconds != null
        ) {
          setElapsedSeconds(response.data.updatedProgress.currentTimerSeconds);
          localStorage.setItem(
            `timer_${slug}`,
            response.data.updatedProgress.currentTimerSeconds.toString(),
          );
        }
      } else {
        // Fallback: reload if backend doesn't send updated data yet
        await loadList();
      }
    } catch (error) {
      setMessageType("error");
      setMessage("Failed to submit guess");
      setGuess("");
    }

    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 5000);
  };

  const handleReset = async () => {
    if (
      window.confirm(
        "Are you sure you want to reset your progress on this list?",
      )
    ) {
      try {
        await guessAPI.resetProgress(list.id);

        // Reset all states
        setGameMode(null);
        setShowModeSelector(true);
        setIsTimerActive(false);
        setElapsedSeconds(0);

        // Clear saved timer from localStorage
        localStorage.removeItem(`timer_${slug}`);

        loadList();
        setMessage("Progress reset successfully!");
        setMessageType("success");
      } catch (error) {
        setMessage("Failed to reset progress");
        setMessageType("error");
      }
    }
  };

  const getSortedItems = () => {
    if (!list || !list.guessedItems) return [];

    let items = [...list.guessedItems];

    switch (sortOrder) {
      case "alphabetical":
        return items.sort((a, b) => a.answer.localeCompare(b.answer));
      case "reverse":
        return items.sort((a, b) => b.answer.localeCompare(a.answer));
      case "rank":
        return items.sort(
          (a, b) => (a.rankPosition || 0) - (b.rankPosition || 0),
        );
      default:
        return items;
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-lg sm:text-xl text-white">Loading...</div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* Error Icon */}
          <div className="mb-8">
            <svg
              className="w-24 h-24 mx-auto text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          {/* Error Message */}
          <h2 className="text-3xl font-bold text-white mb-4">List Not Found</h2>
          <p className="text-gray-400 mb-8">
            The list you're looking for doesn't exist or has been removed.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition"
            >
              Browse All Lists
            </button>
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4 leading-tight">
          {list.title}
        </h1>

        {list.description && (
          <div className="text-gray-400 mb-4 prose prose-invert prose-sm max-w-none text-sm sm:text-base">
            <ReactMarkdown
              components={{
                p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                strong: ({ node, ...props }) => (
                  <strong className="text-white font-semibold" {...props} />
                ),
                em: ({ node, ...props }) => (
                  <em className="text-gray-300" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc list-inside mb-2 space-y-1"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li className="text-gray-400" {...props} />
                ),
              }}
            >
              {list.description}
            </ReactMarkdown>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4">
          {list.categories
            .filter((cat) => cat.name.toLowerCase() !== "ranked")
            .map((cat) => (
              <span
                key={cat.id}
                className="px-2 sm:px-3 py-1 bg-blue-600/20 text-blue-400 text-xs sm:text-sm rounded whitespace-nowrap"
              >
                {cat.name}
              </span>
            ))}
          {list.isRanked && (
            <span className="px-2 sm:px-3 py-1 bg-yellow-600/20 text-yellow-400 text-xs sm:text-sm rounded">
              Ranked
            </span>
          )}
          {gameMode === "timed" && (
            <span className="px-2 sm:px-3 py-1 bg-purple-600/20 text-purple-400 text-xs sm:text-sm rounded">
              Timed Mode
            </span>
          )}
        </div>

        {/* Timer Display */}
        {gameMode === "timed" && !list.isCompleted && (
          <div className="mb-4 bg-purple-500/10 border border-purple-500 rounded-lg p-3 flex items-center justify-center">
            <Timer
              isActive={isTimerActive}
              onTimeUpdate={handleTimeUpdate}
              initialSeconds={elapsedSeconds}
            />
          </div>
        )}

        {/* Current Attempt & Personal Best Display - WHILE PLAYING */}
        {isAuthenticated &&
          !list.isCompleted &&
          gameMode &&
          list.guessedCount > 0 && (
            <div className="mb-4 space-y-2">
              {/* Current Attempt Counter */}
              <div className="bg-blue-500/10 border border-blue-500 text-blue-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <span>
                  Current Attempt: {list.currentAttemptGuesses || 0}{" "}
                  {(list.currentAttemptGuesses || 0) === 1
                    ? "guess"
                    : "guesses"}
                </span>
              </div>

              {/* Personal Best for Current Mode */}
              {gameMode === "normal" && list.bestGuessesNormal && (
                <div className="bg-green-500/10 border border-green-500 text-green-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                  <span>
                    Personal Best: {list.bestGuessesNormal}{" "}
                    {list.bestGuessesNormal === 1 ? "guess" : "guesses"}
                  </span>
                </div>
              )}

              {gameMode === "timed" && list.bestTimeSeconds && (
                <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Best Time: {formatTime(list.bestTimeSeconds)}</span>
                </div>
              )}

              {gameMode === "timed" && list.bestGuessesTimed && (
                <div className="bg-purple-500/10 border border-purple-500 text-purple-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                  <span>
                    Personal Best: {list.bestGuessesTimed}{" "}
                    {list.bestGuessesTimed === 1 ? "guess" : "guesses"}
                  </span>
                </div>
              )}
            </div>
          )}

        {/* Completion Message & Stats - AFTER COMPLETING */}
        {isAuthenticated && list.isCompleted && (
          <div className="mb-4 space-y-2">
            {/* Completion message with guess count */}
            <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded text-sm sm:text-base">
              🎉 Congratulations! Completed in{" "}
              {list.currentAttemptGuesses || list.totalItems}{" "}
              {(list.currentAttemptGuesses || list.totalItems) === 1
                ? "guess"
                : "guesses"}
              !
            </div>

            {/* Show personal bests after completion */}
            {gameMode === "normal" && list.bestGuessesNormal && (
              <div className="bg-green-500/10 border border-green-500 text-green-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                <span>
                  Personal Best: {list.bestGuessesNormal}{" "}
                  {list.bestGuessesNormal === 1 ? "guess" : "guesses"}
                  {list.currentAttemptGuesses === list.bestGuessesNormal && (
                    <span className="ml-2 text-yellow-400">New Record!</span>
                  )}
                </span>
              </div>
            )}

            {gameMode === "timed" && (
              <>
                {list.completionTimeSeconds && (
                  <div className="bg-blue-500/10 border border-blue-500 text-blue-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      Last Time: {formatTime(list.completionTimeSeconds)}
                    </span>
                  </div>
                )}
                {list.bestTimeSeconds && (
                  <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      Best Time: {formatTime(list.bestTimeSeconds)}
                      {list.completionTimeSeconds === list.bestTimeSeconds && (
                        <span className="ml-2 text-yellow-400">
                          New Record!
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {list.bestGuessesTimed && (
                  <div className="bg-purple-500/10 border border-purple-500 text-purple-400 px-4 py-2 rounded text-sm flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <span>
                      Personal Best: {list.bestGuessesTimed}{" "}
                      {list.bestGuessesTimed === 1 ? "guess" : "guesses"}
                      {list.currentAttemptGuesses === list.bestGuessesTimed && (
                        <span className="ml-2 text-yellow-400">
                          New Record!
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="flex justify-between text-xs sm:text-sm text-gray-400 mb-2">
            <span>
              Progress: {list.guessedCount} / {list.totalItems}
            </span>
            <span className="font-medium">
              {Math.round((list.guessedCount / list.totalItems) * 100)}%
            </span>
          </div>
          <div className="w-full bg-dark-bg rounded-full h-2.5 sm:h-3 border border-gray-600">
            <div
              className="bg-blue-600 h-full rounded-full transition-all duration-300"
              style={{
                width: `${(list.guessedCount / list.totalItems) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Mode Selector - Show only if not started */}
        {!list.isCompleted &&
          isAuthenticated &&
          showModeSelector &&
          list.guessedCount === 0 && (
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-3 text-center">
                Choose Game Mode
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => handleModeSelect("normal")}
                  className="bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600 text-blue-400 p-4 rounded-lg transition text-left"
                >
                  <div className="font-semibold text-lg mb-1">Normal Mode</div>
                  <div className="text-sm text-gray-400 mb-2">
                    Play at your own pace. No timer, just enjoy the game!
                  </div>
                  <div className="text-xs text-gray-500 italic">
                    Choose your difficulty: Casual or Relaxed
                  </div>
                </button>
                <button
                  onClick={() => handleModeSelect("timed")}
                  className="bg-purple-600/20 hover:bg-purple-600/30 border border-purple-600 text-purple-400 p-4 rounded-lg transition text-left"
                >
                  <div className="font-semibold text-lg mb-1 flex items-center gap-2">
                    Timed Mode
                  </div>
                  <div className="text-sm text-gray-400 mb-2">
                    Race against the clock! Track your completion time and beat
                    your personal best.
                  </div>
                  <div className="text-xs text-purple-300 italic flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Always uses Relaxed difficulty
                  </div>
                </button>
              </div>
            </div>
          )}

        {/* Difficulty Selector - Show only in Normal Mode */}
        {!list.isCompleted &&
          isAuthenticated &&
          !showModeSelector &&
          gameMode === "normal" && (
            <div className="bg-dark-card border border-gray-700 rounded-lg p-3 sm:p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">
                    Difficulty:
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDifficultyMode("casual")}
                      className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition ${
                        difficultyMode === "casual"
                          ? "bg-blue-600 text-white"
                          : "bg-dark-bg text-gray-400 hover:text-white border border-gray-600"
                      }`}
                    >
                      Casual
                    </button>
                    <button
                      onClick={() => setDifficultyMode("relaxed")}
                      className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition ${
                        difficultyMode === "relaxed"
                          ? "bg-green-600 text-white"
                          : "bg-dark-bg text-gray-400 hover:text-white border border-gray-600"
                      }`}
                    >
                      Relaxed
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => setShowDifficultyInfo(!showDifficultyInfo)}
                  className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 transition self-end sm:self-auto"
                >
                  <span>
                    {showDifficultyInfo ? "Hide Info" : "What's this?"}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      showDifficultyInfo ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {showDifficultyInfo && (
                <div className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-400 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                    <span className="text-blue-400 font-semibold sm:min-w-[70px]">
                      Casual:
                    </span>
                    <span>
                      Exact match (primary answer only), ignores accents.{" "}
                      <span className="text-gray-500 block sm:inline">
                        "Beyonce" = "Beyoncé"
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                    <span className="text-green-400 font-semibold sm:min-w-[70px]">
                      Relaxed:
                    </span>
                    <span>
                      90% similarity, typos OK, ignores special characters,
                      accepts alternative answers.{" "}
                      <span className="text-gray-500 block sm:inline">
                        "Example" = "Example.", "Swimming" = "Swiming", "Messi"
                        = "Lionel Messi"
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Info banner for Timed Mode */}
        {!list.isCompleted &&
          isAuthenticated &&
          !showModeSelector &&
          gameMode === "timed" && (
            <div className="bg-purple-500/10 border border-purple-500 rounded-lg p-3 mb-6">
              <div className="flex items-center gap-2 text-purple-300 text-sm">
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  <strong>Timed Mode:</strong> Using Relaxed difficulty (90%
                  similarity, typos OK)
                </span>
              </div>
            </div>
          )}
      </div>

      {/* Guess Input - Show only after mode is selected */}
      {!list.isCompleted && isAuthenticated && !showModeSelector && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
          <form onSubmit={handleGuess}>
            <label className="block text-gray-300 mb-2 font-semibold text-sm sm:text-base">
              Enter your guess:
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Type your answer..."
                className="flex-1 px-4 py-3 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500 transition"
                autoFocus
              />
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition whitespace-nowrap text-sm sm:text-base"
              >
                Guess
              </button>
            </div>
          </form>

          {message && (
            <div
              className={`mt-4 px-4 py-3 rounded text-sm sm:text-base ${
                messageType === "success"
                  ? "bg-green-500/10 border border-green-500 text-green-500"
                  : "bg-red-500/10 border border-red-500 text-red-500"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      )}

      {!isAuthenticated && (
        <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded mb-6 text-sm sm:text-base">
          You must be{" "}
          <a href="/login" className="underline font-semibold">
            logged in
          </a>{" "}
          to guess items.
        </div>
      )}

      <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {isAdmin ? "All Items" : "Your Guessed Items"}
          </h2>

          {list.isRanked && list.guessedItems.length > 0 && (
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
            >
              <option value="alphabetical">A → Z</option>
              <option value="reverse">Z → A</option>
              <option value="rank">By Rank</option>
            </select>
          )}
        </div>

        {list.guessedCount === 0 ? (
          <p className="text-gray-400 text-center py-8 text-sm sm:text-base">
            No items guessed yet. Start guessing above!
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {getSortedItems().map((item) => (
              <div
                key={item.id}
                className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg flex justify-between items-center gap-2"
              >
                <span className="text-white font-medium text-sm sm:text-base truncate">
                  {item.answer}
                </span>
                {list.isRanked && item.rankPosition && (
                  <span className="text-gray-400 text-xs sm:text-sm flex-shrink-0">
                    #{item.rankPosition}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isAuthenticated && list.guessedCount > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={handleReset}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg transition text-sm sm:text-base font-medium"
          >
            Reset Progress
          </button>
        </div>
      )}
    </div>
  );
};

export default ListDetail;
